'use client';

import * as React from 'react';
import { Minus } from 'lucide-react';
import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
import { DigitSlot } from '@/components/digit-slot';
import { cn } from '@/lib/utils';

export interface InputOTPProps {
	/** The code so far. Controlled: pair it with `onChange`. */
	value?: string;
	onChange?: (value: string) => void;
	/** Fires once when the last box is filled - submit from here. */
	onComplete?: (value: string) => void;
	/** Number of boxes. */
	length?: number;
	/** Digits only (the default): other keys and pasted letters are refused. `false` also takes letters. */
	numeric?: boolean;
	/** Draws a dash after this many boxes, e.g. 3 for a "123–456" code. */
	separatorAfter?: number;
	disabled?: boolean;
	autoFocus?: boolean;
	id?: string;
	name?: string;
	/** Required unless `aria-labelledby` is given: the boxes have no visible label of their own. */
	'aria-label'?: string;
	'aria-labelledby'?: string;
	'aria-describedby'?: string;
	/** On the outer row. */
	className?: string;
}

/**
 * The one-time-code field: `input-otp`'s `OTPInput` drawn with `DigitSlot` boxes, with the rules
 * every code field needs already applied - numeric keyboard, digits only, `one-time-code`
 * autofill (SMS and password managers), `maxLength` from `length`, and the `group` class the
 * boxes' hover/focus borders read. One real <input> sits underneath, so paste, autofill and
 * screen readers behave as they do on any field.
 */
export const InputOTP = React.forwardRef<HTMLInputElement, InputOTPProps>(
	({ value, onChange, onComplete, length = 6, numeric = true, separatorAfter, disabled, className, ...props }, ref) => (
		<OTPInput
			ref={ref}
			value={value}
			onChange={onChange}
			onComplete={onComplete}
			maxLength={length}
			inputMode={numeric ? 'numeric' : 'text'}
			pattern={numeric ? REGEXP_ONLY_DIGITS : undefined}
			autoComplete="one-time-code"
			disabled={disabled}
			containerClassName={cn('group flex items-center has-[:disabled]:opacity-30', className)}
			render={({ slots }) => (
				<div className="flex items-center gap-2">
					{slots.map((slot, idx) => (
						<React.Fragment key={idx}>
							{separatorAfter && idx === separatorAfter && idx < slots.length ? <Minus className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> : null}
							<DigitSlot {...slot} />
						</React.Fragment>
					))}
				</div>
			)}
			{...props}
		/>
	),
);
InputOTP.displayName = 'InputOTP';
